/**
 * 配置管理前端接口
 */

import { request } from '@/utils/http'
import {
  type BatchDeleteRequest,
  type PageResponse
} from '@/api/types/common'

// API基础路径
const API_BASE_CONFIG = '/sys/config'
const API_BASE_CONFIG_DETAIL = '/sys/configDetail'

// ==================== 配置定义相关模型 ====================

/**
 * 配置定义VO模型
 */
export interface ConfigVO {
  /** 主键ID */
  id?: string | null
  /** 租户ID */
  tenantId?: string | null
  /** 创建人 */
  createdBy?: string | null
  /** 创建时间 */
  dateCreated?: string | null
  /** 修改人 */
  modifiedBy?: string | null
  /** 修改时间 */
  dateModified?: string | null
  /** 版本号 */
  version?: number | null
  /** 逻辑删除标识：0-未删除，1-已删除 */
  deleted?: number | null
  /** 配置名称 */
  configName?: string | null
  /** 默认配置值 */
  configValue?: string | null
  /** 数据类型 1-字符串 2-数值 3-布尔值 */
  dataType?: number | null
  /** 企业ID */
  enterpriseId?: string | null
  /** 分公司ID */
  officeId?: string | null
  /** 是否可覆盖 */
  overrideable?: boolean | null
  /** 可选值列表 */
  literals?: string | null
  /** 备注 */
  remarks?: string | null
  /** 当前值 */
  currentValue?: string | null
  /** 当前配置详情 */
  configDetail?: ConfigDetailVO | null
}

/**
 * 配置定义查询请求参数
 */
export interface ConfigQueryRequest {
  /** 当前页码，默认为1 */
  pageNum?: number | null
  /** 每页大小，默认为10 */
  pageSize?: number | null
  /** 配置名称 */
  configName?: string | null
  /** 企业ID */
  enterpriseId?: string | null
  /** 分公司ID */
  officeId?: string | null
  /** 数据类型 1-字符串 2-数值 3-布尔值 */
  dataType?: number | null
  /** 关键词搜索 */
  keyword?: string | null
}

/**
 * 配置定义保存请求参数
 */
export interface ConfigSaveRequest extends ConfigVO {
}

// ==================== 配置定义相关API ====================

/**
 * 分页查询配置定义列表
 */
export const pageConfig = (params: ConfigQueryRequest) => {
  return request.post<PageResponse<ConfigVO>>(`${API_BASE_CONFIG}/page`, params)
}

/**
 * 查询配置定义列表
 */
export const listConfig = (params: ConfigQueryRequest) => {
  return request.post<ConfigVO[]>(`${API_BASE_CONFIG}/list`, params)
}

/**
 * 根据条件获取配置定义详情
 */
export const getConfig = (params: ConfigQueryRequest) => {
  return request.post<ConfigVO>(`${API_BASE_CONFIG}/get`, params)
}

/**
 * 新增配置定义
 */
export const saveConfig = (params: ConfigSaveRequest) => {
  return request.post<boolean>(`${API_BASE_CONFIG}/save`, params)
}

/**
 * 更新配置定义
 */
export const updateConfig = (params: ConfigSaveRequest) => {
  return request.post<boolean>(`${API_BASE_CONFIG}/update`, params)
}

/**
 * 删除配置定义
 */
export const deleteConfig = (id: string) => {
  return request.post<boolean>(`${API_BASE_CONFIG}/delete`, { id })
}

/**
 * 批量删除配置定义
 */
export const batchDeleteConfig = (params: BatchDeleteRequest) => {
  return request.post<boolean>(`${API_BASE_CONFIG}/batchDelete`, params)
}

/**
 * 根据配置名称查询配置值
 * @param configName 配置名称
 * @returns 配置值
 */
export const getConfigValue = (configName: string) => {
  return request.post<string>(`${API_BASE_CONFIG}/getConfigValue`, { configName })
}

// ==================== 配置详情相关模型 ====================

/**
 * 配置详情VO模型
 */
export interface ConfigDetailVO {
  /** 主键ID */
  id?: string | null
  /** 租户ID */
  tenantId?: string | null
  /** 创建人 */
  createdBy?: string | null
  /** 创建时间 */
  dateCreated?: string | null
  /** 修改人 */
  modifiedBy?: string | null
  /** 修改时间 */
  dateModified?: string | null
  /** 版本号 */
  version?: number | null
  /** 逻辑删除标识：0-未删除，1-已删除 */
  deleted?: number | null
  /** 配置定义ID */
  configDefId?: string | null
  /** 配置名称 */
  configName?: string | null
  /** 配置值 */
  configValue?: string | null
  /** 数据类型 1-字符串 2-数值 3-布尔值 */
  dataType?: number | null
  /** 企业ID */
  enterpriseId?: string | null
  /** 分公司ID */
  officeId?: string | null
  /** 是否可覆盖 */
  overrideable?: boolean | null
  /** 可选值列表 */
  literals?: string | null
  /** 备注 */
  remarks?: string | null
  /** 当前值 */
  currentValue?: string | null
}

/**
 * 配置详情查询请求参数
 */
export interface ConfigDetailQueryRequest {
  /** 主键ID */
  id?: string | null
  /** 当前页码，默认为1 */
  pageNum?: number | null
  /** 每页大小，默认为10 */
  pageSize?: number | null
  /** 配置定义ID */
  configDefId?: string | null
  /** 配置名称 */
  configName?: string | null
  /** 企业ID */
  enterpriseId?: string | null
  /** 分公司ID */
  officeId?: string | null
  /** 数据类型 1-字符串 2-数值 3-布尔值 */
  dataType?: number | null
  /** 关键词搜索 */
  keyword?: string | null
}

/**
 * 配置详情保存请求参数
 */
export interface ConfigDetailSaveRequest extends ConfigDetailVO {
}

// ==================== 配置详情相关API ====================

/**
 * 分页查询配置详情列表
 */
export const pageConfigDetail = (params: ConfigDetailQueryRequest) => {
  return request.post<PageResponse<ConfigDetailVO>>(`${API_BASE_CONFIG_DETAIL}/page`, params)
}

/**
 * 查询配置详情列表
 */
export const listConfigDetail = (params: ConfigDetailQueryRequest) => {
  return request.post<ConfigDetailVO[]>(`${API_BASE_CONFIG_DETAIL}/list`, params)
}

/**
 * 根据条件获取配置详情
 */
export const getConfigDetail = (params: ConfigDetailQueryRequest) => {
  return request.post<ConfigDetailVO>(`${API_BASE_CONFIG_DETAIL}/get`, params)
}

/**
 * 新增配置详情
 */
export const saveConfigDetail = (params: ConfigDetailSaveRequest) => {
  return request.post<boolean>(`${API_BASE_CONFIG_DETAIL}/save`, params)
}

/**
 * 更新配置详情
 */
export const updateConfigDetail = (params: ConfigDetailSaveRequest) => {
  return request.post<boolean>(`${API_BASE_CONFIG_DETAIL}/update`, params)
}

/**
 * 删除配置详情
 */
export const deleteConfigDetail = (id: string) => {
  return request.post<boolean>(`${API_BASE_CONFIG_DETAIL}/delete`, { id })
}

/**
 * 批量删除配置详情
 */
export const batchDeleteConfigDetail = (params: BatchDeleteRequest) => {
  return request.post<boolean>(`${API_BASE_CONFIG_DETAIL}/batchDelete`, params)
}
